import React, { useRef, useEffect, useState } from "react"
  ;
import { QRCodeSVG } from "qrcode.react";
import QRCode from 'qrcode';
import { formatStatusText, capitalizeWords } from "../utils/formatters";
import {
  PrinterIcon,
  DocumentArrowDownIcon,
  XMarkIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { Order } from "../feature/orders/order";
import { apiService } from "../services/api";
import { printHTML } from "../utils/printUtils";
import { generateQRLayout } from "../utils/qrLayoutGenerator";
import DropdownField from "@/form/DropdownField";

interface QRCodePrintViewProps {
  order: Order;
  onClose: () => void;
}

// Interface for QR code data from API (matches backend camelCase response)
interface QRCodeItem {
  id: string;
  qrCode: string; // QR data string from backend
  qrImageData?: string | null; // Base64 data URL from backend (pre-generated)
  qrImageDetails?: { // Additional QR code information
    size: string;
    dimensions: {
      width: number;
      height: number;
      scale: number;
    };
    recommendedPrintSize: string;
    printOptimized: boolean;
    generatedOnTheFly?: boolean;
  };
  itemNumber: string;
  type: string;
  currentStage: string;
  status: string;
  specifications: any;
  width?: string | number;
  height?: string | number;
  color?: string | null;
  createdAt?: any;
  product: {
    id: string;
    name: string;
    type: string;
    color?: string | null;
  };
  // Legacy snake_case support (for backward compatibility)
  qr_code?: string;
  qr_code_image_url?: string | null;
  qr_image_data?: string | null;
  item_number?: string;
  current_stage?: string;
  created_at?: string;
}

interface OrderQRData {
  order: {
    id: string;
    orderNumber: string;
    orderNumberNew: string;
    customerName: string;
    siteNamePlate?: string | null;
    // Legacy snake_case support
    order_number?: string;
    order_number_new?: string;
    customer_name?: string;
    site_name_plate?: string | null;
  };
  items: QRCodeItem[];
}

const itemOption = [
  { label: 'All Items', value: 'all' },
  { label: 'Frame', value: 'Frame' },
  { label: 'Panel', value: 'Panel' },
  { label: 'Panel Window', value: 'Panel Window' },
  { label: 'Net Fixing', value: 'Net Fixing' },
]

const QRCodePrintView: React.FC<QRCodePrintViewProps> = ({
  order,
  onClose,
}) => {
  const printRef = useRef<HTMLDivElement>(null);
  const [qrData, setQrData] = useState<OrderQRData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string>('large'); // Default to large for print quality
  const [showIndividualPrint, setShowIndividualPrint] = useState(true);
  const [qrCodeImages, setQrCodeImages] = useState<{ [key: string]: string }>({});
  const [allItems, setAllItems] = useState<QRCodeItem[]>([]);
  const [itemFilter, setItemFilter] = useState<string>('all');

  // Compute filtered items based on itemFilter
  const filteredItems = allItems?.filter((item: QRCodeItem) => itemFilter === 'all' || item.type === itemFilter) || [];

  useEffect(() => {
    console.log("allItems: ", allItems);
  }, [allItems])

  // Debug logging
  console.log('QRCodePrintView received order:', order);
  console.log('Order ID:', order?.id);
  console.log('Order properties:', Object.keys(order || {}));

  const displayOrderNumber = order?.orderNumberNew || order?.orderNumber || order?.id?.slice(-6);

  // Generate QR code images at runtime
  const generateQRCodeImage = async (qrData: string, itemId: string) => {
    try {
      const size = selectedSize === 'small' ? 120 : selectedSize === 'medium' ? 180 : 240;
      const qrCodeDataURL = await QRCode.toDataURL(qrData, {
        width: size,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'H' // High error correction for better quality
      });
      return qrCodeDataURL;
    } catch (error) {
      console.error('Error generating QR code:', error);
      return null;
    }
  };

  // Generate QR codes for all items when data changes
  useEffect(() => {
    const generateAllQRCodes = async () => {
      if (!allItems || allItems.length === 0) {
        console.log('No items to generate QR codes for');
        return;
      }

      console.log(`Generating QR codes for ${allItems.length} items`);
      const qrImages: { [key: string]: string } = {};

      for (const item of allItems) {
        // First, try to use pre-generated image from backend (qrImageData - camelCase)
        if (item.qrImageData) {
          console.log(`Using pre-generated QR image for item ${item.id}`);
          qrImages[item.id] = item.qrImageData;
        }
        // Fallback: use legacy snake_case field
        else if (item.qr_image_data) {
          console.log(`Using pre-generated QR image (legacy) for item ${item.id}`);
          qrImages[item.id] = item.qr_image_data;
        }
        // Fallback: use qr_code_image_url if available
        else if (item.qr_code_image_url) {
          console.log(`Using qr_code_image_url for item ${item.id}`);
          qrImages[item.id] = item.qr_code_image_url;
        }
        // Last resort: generate from qrCode or qr_code string
        else if (item.qrCode || item.qr_code) {
          const qrCodeString = item.qrCode || item.qr_code;
          console.log(`Generating QR code from string for item ${item.id}`);
          const qrImage = await generateQRCodeImage(qrCodeString, item.id);
          if (qrImage) {
            qrImages[item.id] = qrImage;
            console.log(`Successfully generated QR code for item ${item.id}`);
          } else {
            console.warn(`Failed to generate QR code for item ${item.id}`);
          }
        } else {
          console.warn(`No QR code data available for item ${item.id}`);
        }
      }

      console.log(`Generated ${Object.keys(qrImages).length} QR code images`);
      setQrCodeImages(qrImages);
    };

    generateAllQRCodes();
  }, [allItems, selectedSize]);

  // Fetch QR codes for this order
  useEffect(() => {
    const fetchQRCodes = async () => {
      try {
        // Validate order object
        if (!order || !order.id) {
          console.error('Invalid order object:', order);
          setError('Invalid order data: missing order ID');
          setLoading(false);
          return;
        }

        console.log('Fetching QR codes for order:', order.id);
        setLoading(true);

        // Use the API service instead of raw fetch
        const result = await apiService.getOrderQRCodes(order.id);

        if (result.success && result.data) {
          console.log('✅ Successfully fetched QR codes:', result.data);
          console.log('📦 Items count:', result.data.items?.length);
          if (result.data.items && result.data.items.length > 0) {
            console.log('📋 Sample item:', {
              id: result.data.items[0].id,
              has_qrCode: !!result.data.items[0].qrCode,
              has_qrImageData: !!result.data.items[0].qrImageData,
              qrCode: result.data.items[0].qrCode?.substring(0, 20) + '...',
            });
          }
          setQrData(result.data);
        } else {
          // Handle API service error response
          const errorMessage = result.error?.message || 'Failed to fetch QR codes';
          console.error('API Error:', result.error);
          throw new Error(errorMessage);
        }
      } catch (err) {
        console.error('Error fetching QR codes:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchQRCodes();
  }, [order?.id]);

  // const handlePrint = () => {
  //   const printContent = printRef.current;
  //   if (!printContent) return;

  //   const printWindow = window.open("", "_blank");
  //   if (!printWindow) return;

  //   printWindow.document.write(`
  //     <!DOCTYPE html>
  //     <html>
  //       <head>
  //         <title>QR Codes - Order ${order.orderNumberNew || order.orderNumber || order.order_tracking_code || order.id.slice(-6)}</title>
  //         <style>
  //           body {
  //             font-family: Arial, sans-serif;
  //             margin: 0;
  //             padding: 20px;
  //             background: white;
  //           }
  //           .header {
  //             text-align: center;
  //             margin-bottom: 30px;
  //             border-bottom: 2px solid #000;
  //             padding-bottom: 20px;
  //           }
  //           .qr-grid {
  //             display: flex;
  //             flex-wrap: wrap;
  //             gap: 15px;
  //             margin: 20px 0;
  //             justify-content: flex-start;
  //           }
  //           .qr-item {
  //             border: 2px solid #000;
  //             padding: 15px;
  //             text-align: center;
  //             background: white;
  //             page-break-inside: avoid;
  //             display: flex;
  //             flex-direction: row;
  //             align-items: center;
  //             min-width: 280px;
  //             max-width: 350px;
  //             flex: 0 0 auto;
  //           }
  //           .qr-left {
  //             width: 100px;
  //             text-align: center;
  //             margin-right: 10px;
  //           }
  //           .qr-code {
  //             margin: 0;
  //             max-width: 80px;
  //             max-height: 80px;
  //           }
  //           .qr-code img {
  //             width: 100%;
  //             height: auto;
  //             max-width: 80px;
  //             max-height: 80px;
  //           }
  //           .qr-right {
  //             flex: 1;
  //             text-align: left;
  //           }
  //           .item-info {
  //             margin-top: 5px;
  //             font-size: 11px;
  //             line-height: 1.3;
  //           }
  //           .item-title {
  //             font-weight: bold;
  //             font-size: 13px;
  //             margin-bottom: 6px;
  //           }
  //           .qr-text {
  //             font-family: monospace;
  //             font-size: 9px;
  //             margin-top: 5px;
  //             word-break: break-all;
  //             background: #f5f5f5;
  //             padding: 3px;
  //             border-radius: 2px;
  //           }
  //           @media print {
  //             body { 
  //               margin: 0; 
  //               padding: 10px;
  //               font-size: 12px;
  //             }
  //             .qr-grid { 
  //               gap: 10px;
  //             }
  //             .qr-item { 
  //               border: 1px solid #000; 
  //               padding: 10px;
  //               min-width: 250px;
  //               max-width: 300px;
  //             }
  //             .qr-left {
  //               width: 80px;
  //               margin-right: 8px;
  //             }
  //             .qr-code {
  //               max-width: 70px;
  //               max-height: 70px;
  //             }
  //             .qr-code img {
  //               max-width: 70px;
  //               max-height: 70px;
  //             }
  //           }
  //         </style>
  //       </head>
  //       <body>
  //         <div class="header">
  //           <h1>Manufacturing QR Codes</h1>
  //           <p>Order ${qrData?.order?.order_number_new || qrData?.order?.order_number || order.id.slice(-6)} - ${qrData?.order?.customer_name || order.customer?.name}</p>
  //           <p>Generated on ${new Date().toLocaleDateString()}</p>
  //         </div>
  //         <div class="qr-grid">
  //           ${allItems?.map((item, index) => `
  //             <div class="qr-item">
  //               <div class="qr-left">
  //                 <div class="qr-code">
  //                   ${qrCodeImages[item.id] ? `<img src="${qrCodeImages[item.id]}" alt="QR Code" />` : '<div style="width: 80px; height: 80px; background: #f0f0f0; display: flex; align-items: center; justify-content: center; font-size: 10px;">Loading...</div>'}
  //                 </div>
  //               </div>
  //               <div class="qr-right">
  //                 <div class="item-title">${item.item_number}</div>
  //                 <div class="item-info">
  //                   <div><strong>Type:</strong> ${item.type}</div>
  //                   <div><strong>Product:</strong> ${item.product?.name || item.product?.type || '-'}</div>
  //                   <div><strong>Order:</strong> ${qrData?.order?.order_number_new || qrData?.order?.order_number || order.id.slice(-6)}</div>
  //                   <div><strong>Customer:</strong> ${qrData?.order?.customer_name || order.customer?.name || '-'}</div>
  //                   <div><strong>Dimensions:</strong> ${
  //                     (() => {
  //                       const width = (item as any)?.width || (item as any)?.specifications?.width || (item as any)?.product?.width || (item as any)?.dimensions?.width;
  //                       const height = (item as any)?.height || (item as any)?.specifications?.height || (item as any)?.product?.height || (item as any)?.dimensions?.height;
  //                       return width && height ? `${Math.round(width)}x${Math.round(height)}` : '-';
  //                     })()
  //                   }</div>
  //                   <div><strong>Color:</strong> ${
  //                     (item as any)?.color || 
  //                     (item as any)?.specifications?.color || 
  //                     (item as any)?.specifications?.colour || 
  //                     (item as any)?.product?.color || 
  //                     (item as any)?.product?.colour || 
  //                     '-'
  //                   }</div>
  //                 </div>
  //               </div>
  //             </div>
  //           `).join('')}
  //         </div>
  //       </body>
  //     </html>
  //   `);

  //   printWindow.document.close();
  //   printWindow.focus();

  //   // Small delay to ensure content is loaded before printing
  //   setTimeout(() => {
  //     printWindow.print();
  //     printWindow.close();
  //   }, 250);
  // };

  //   const handlePrint = () => {
  //     const printContent = printRef.current;
  //     if (!printContent) return;

  //     // Create hidden iframe
  //     const iframe = document.createElement('iframe');
  //     iframe.style.position = 'absolute';
  //     iframe.style.width = '0';
  //     iframe.style.height = '0';
  //     iframe.style.border = 'none';
  //     document.body.appendChild(iframe);

  //     console.log("allItems: ", allItems);

  //     const doc = iframe.contentWindow?.document;
  //     if (!doc) return;

  //     doc.open();
  //     doc.write(`
  // <!DOCTYPE html>
  // <html>
  //   <head>
  //     <style>
  //       /* Reset all spacing */
  //       @page {
  //         size: 50mm 20mm; /* width x height with landscape orientation */
  //       }

  //       html, body {
  //         margin: 0;
  //         padding: 3px;
  //         padding-top: 0;
  //         width: 50mm;
  //         height: 20mm;
  //         background: white;
  //         font-family: Arial, sans-serif;
  //         font-size: 8px; /* Slightly larger font for better readability */
  //         line-height: 1.2;
  //       }

  //       .qr-item {
  //         width: 100%;
  //         height: 100%;
  //         box-sizing: border-box;
  //         padding: 1mm;
  //         display: flex;
  //         align-items: center;
  //         justify-content: space-between;
  //         border: none;
  //         page-break-after: always; /* force each QR to start on new page */
  //         // transform: rotate(90deg); /* Rotate 90 degrees clockwise */
  //         // transform-origin: center; /* Rotate from center */
  //       }

  //       .qr-code {
  //         flex-shrink: 0;
  //       }

  //       .qr-code img {
  //         width: 16mm;
  //         height: 16mm;

  //         object-fit: contain;
  //       }

  //       .item-details {
  //         flex: 1;
  //         margin-left: 2mm;
  //         font-size: 10px; /* Larger font for better readability */
  //         line-height: 1.3;
  //         padding:1mm;
  //       }

  //       .item-title {
  //         // font-weight: bold;
  //         font-size: 9px;
  //        padding-top: 1px;
  //       }

  //       .item-info div {
  //         margin-bottom: 0.5px;
  //       }

  //       @media print {
  //         body {
  //           margin: 0;
  //           padding: 0;
  //         }
  //       }
  //     </style>
  //   </head>
  //   <body>
  //     ${allItems?.map(item => `
  //       <div class="qr-item">
  //         <div class="qr-code">
  //           ${qrCodeImages[item.id]
  //             ? `<img src="${qrCodeImages[item.id]}" alt="QR Code" />`
  //             : '<div style="width: 16mm; height: 16mm; background: #f0f0f0; display: flex; align-items: center; justify-content: center; font-size: 5px;">Loading...</div>'}
  //         </div>
  //         <div class="item-details">
  //           <div class="item-title">${item.item_number}</div>
  //           <div>${qrData?.order?.customer_name || order.customer?.name || '-'}</div>
  //           <div> ${item?.product?.type} - ${item.type}</div>
  //           <div>${item.product?.name || item.product?.type || '-'}</div>

  //           <div>${(() => {
  //             const width = (item as any)?.width || (item as any)?.specifications?.width || (item as any)?.product?.width || (item as any)?.dimensions?.width;
  //             const height = (item as any)?.height || (item as any)?.specifications?.height || (item as any)?.product?.height || (item as any)?.dimensions?.height;
  //             return width && height ? `${Math.round(width)} W x ${Math.round(height)} H` : '-';
  //           })()} </div>
  //           <div>${(item as any)?.color ||
  //             (item as any)?.specifications?.color ||
  //             (item as any)?.specifications?.colour ||
  //             (item as any)?.product?.color ||
  //             (item as any)?.product?.colour || '-'} </div>
  //         </div>
  //       </div>
  //     `).join('')}
  //   </body>
  // </html>
  // `);

  //     doc.close();

  //     // Delay to let iframe render
  //     setTimeout(() => {
  //       iframe.contentWindow?.focus();
  //       iframe.contentWindow?.print();
  //       document.body.removeChild(iframe); // Clean up after printing
  //     }, 300);
  //   };

  const handlePrint = () => {
    // const html = generateQRLayout({ allItems, qrCodeImages, qrData, order });

    // printHTML({
    //   title: 'QR Labels',
    //   pageSize: '50mm 20mm',
    //   content: html,
    // });
    const html = generateQRLayout({
      allItems: filteredItems,
      qrCodeImages,
      qrData,
      order,
    });

    printHTML({
      title: "All QR Labels",
      pageSize: "50mm 25mm",
      content: html,
    });
  }

  const handlePrintSingle = (item: any) => {
    console.log('Print QR for item:', item);
    const html = generateQRLayout({
      singleItem: item,
      qrCodeImages,
      qrData,
      order,
    });

    printHTML({
      title: `QR - ${item.itemNumber || item.item_number}`,
      pageSize: "50mm 25mm",
      content: html,
    });
  };



  const regenerateQRCodes = async () => {
    if (!order?.id) {
      console.error('No order ID available for regeneration');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('🔄 Regenerating QR codes for order:', order.id);

      // Call the regenerate endpoint to actually regenerate QR codes
      const result = await apiService.regenerateOrderQRCodes(order.id);

      if (result.success && result.data) {
        console.log('✅ QR codes regenerated successfully:', result.data);
        console.log('🔍 Backend response structure:', {
          hasOrder: !!result.data.order,
          hasResults: !!result.data.results,
          hasItems: !!result.data.items,
          resultsLength: result.data.results?.length,
          itemsLength: result.data.items?.length
        });

        // After regeneration, refetch QR codes to get the proper structure with qrImageData
        const refetchResult = await apiService.getOrderQRCodes(order.id);

        if (refetchResult.success && refetchResult.data) {
          setQrData(refetchResult.data);
        } else {
          // Fallback: Transform backend response to match frontend interface (camelCase)
          const transformedData = {
            order: {
              id: result.data.order.id,
              orderNumber: result.data.order.orderNumber,
              orderNumberNew: result.data.order.orderNumber,
              customerName: result.data.order.customerName
            },
            items: result.data.results.map((item: any) => ({
              id: item.itemId,
              qrCode: item.qrCode,
              qrImageData: item.qrImage?.dataUrl || item.qrImage?.url || null,
              itemNumber: item.itemNumber || null,
              type: item.itemType,
              currentStage: 'pending',
              status: 'pending',
              specifications: {},
              createdAt: new Date().toISOString(),
              product: {
                id: item.productId || '',
                name: item.productName || '',
                type: item.itemType
              }
            }))
          };

          setQrData(transformedData);
        }
        setError(null);
        setSuccess(`Successfully regenerated QR codes for ${result.data.results?.length || 0} items`);

        // Clear success message after 5 seconds
        setTimeout(() => setSuccess(null), 5000);
      } else {
        console.error('❌ Failed to regenerate QR codes:', result.error);
        setError(result.error?.message || 'Failed to regenerate QR codes');
      }
    } catch (err) {
      console.error('💥 Error regenerating QR codes:', err);
      setError('Failed to regenerate QR codes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const downloadQRCodes = () => {
    if (!qrData) return;

    // Create a downloadable HTML file with all QR codes
    const allItems = qrData.items.map((item) => ({
      ...item,
      productType: item.product.type
    }));

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Codes - Order ${order.orderNumberNew || order.orderNumber || order.order_tracking_code || order.id.slice(-6)}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .qr-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 20px; }
            .qr-item {padding: 2px; text-align: center; }
            .qr-code { margin: 10px 0; }
            .item-info { margin-top: 10px; font-size: 12px; }
            .item-title { font-weight: bold; font-size: 14px; margin-bottom: 8px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Manufacturing QR Codes</h1>
            <p>Order ${qrData?.order?.order_number_new || qrData?.order?.order_number || order.id.slice(-6)} - ${qrData?.order?.customer_name || order.customer?.name}</p>
            <p>Generated on ${new Date().toLocaleDateString()}</p>
          </div>
          <div class="qr-grid">
            ${allItems
        .map(
          (item, index) => `
              <div class="qr-item">
                <div class="item-title">${item.type} #${index + 1}</div>
                <div class="qr-code">
                  <svg width="180" height="180" xmlns="http://www.w3.org/2000/svg">
                    <rect width="180" height="180" fill="white" stroke="black"/>
                    <text x="90" y="90" text-anchor="middle" fill="black" font-size="8">${item.qr_code
            }</text>
                  </svg>
                </div>
                <div class="item-info">
                  <div><strong>Product Type:</strong> ${item.productType}</div>
                  <div><strong>Type:</strong> ${item.type}</div>
                  <div><strong>Item Number:</strong> ${item.item_number}</div>
                  <div><strong>Current Stage:</strong> ${item.current_stage}</div>
                  <div><strong>QR Code:</strong> ${item.qr_code}</div>
                </div>
              </div>
            `
        )
        .join("")}
          </div>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `QR_Codes_Order_${order.id.slice(-6)}.html`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Update allItems when qrData changes
  useEffect(() => {
    if (qrData?.items) {
      const items = qrData.items.map((item) => ({
        ...item,
        // Add productType for compatibility with qrLayoutGenerator
        productType: item.product?.type || ''
      }));
      setAllItems(items as any);
    }
  }, [qrData]);

  // Show loading state
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
        <div className="bg-white dark:bg-secondary-800 rounded-xl p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-secondary-600 dark:text-secondary-400">Loading QR codes...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="fixed inset-0 bg-black  flex items-center justify-center p-2 sm:p-4 z-50">
        <div className="bg-white dark:bg-secondary-800 rounded-xl p-8 text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">Error Loading QR Codes</h3>
          <p className="text-secondary-600 dark:text-secondary-400 mb-4">{error}</p>
          <div className="mb-4 p-3 bg-gray-100 rounded text-left text-sm">
            <strong>Debug Info:</strong><br />
            Order ID: {order?.id || 'undefined'}<br />
            Order Type: {typeof order}<br />
            Order Keys: {order ? Object.keys(order).join(', ') : 'null'}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-secondary-500 text-white rounded hover:bg-secondary-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Show message if order ID is missing
  if (!order?.id) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
        <div className="bg-white dark:bg-secondary-800 rounded-xl p-8 text-center max-w-md">
          <div className="text-yellow-500 text-6xl mb-4">⚠️</div>
          <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">Invalid Order Data</h3>
          <p className="text-secondary-600 dark:text-secondary-400 mb-4">
            The order object is missing an ID. This usually means the order data wasn't loaded properly.
          </p>
          <div className="mb-4 p-3 bg-gray-100 rounded text-left text-sm">
            <strong>Debug Info:</strong><br />
            Order ID: {order?.id || 'undefined'}<br />
            Order Type: {typeof order}<br />
            Order Keys: {order ? Object.keys(order).join(', ') : 'null'}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-secondary-500 text-white rounded hover:bg-secondary-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Don't render if no QR data
  if (!qrData) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-[2px] flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white dark:bg-secondary-800 rounded-xl max-w-6xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden">
        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4 mx-4 mt-4">
            <p className="text-green-800 text-center">{success}</p>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-2 sm:p-2 border-b border-secondary-200 dark:border-secondary-700 space-y-3 sm:space-y-0">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl sm:text-2xl font-semibold text-secondary-900 dark:text-white truncate">
              QR Codes for Order {qrData.order.orderNumberNew || qrData.order.order_number_new || qrData.order.orderNumber || qrData.order.order_number}
            </h2>
            <p className="text-secondary-600 dark:text-secondary-400 text-sm sm:text-base truncate">
              Customer: {qrData.order.customerName || qrData.order.customer_name} • {filteredItems?.length} items
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button
              onClick={downloadQRCodes}
              className="flex items-center justify-center px-3 sm:px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-700 transition-colors text-sm"
            >
              <DocumentArrowDownIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              <span className="hidden sm:inline">Download HTML</span>
              <span className="sm:hidden">Download</span>
            </button>
            <button
              onClick={regenerateQRCodes}
              disabled={loading}
              className={`flex items-center justify-center px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm ${loading
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700'
                }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  <span className="hidden sm:inline">Regenerating...</span>
                  <span className="sm:hidden">...</span>
                </>
              ) : (
                <>
                  <ArrowPathIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  <span className="hidden sm:inline">Regenerate QR</span>
                  <span className="sm:hidden">Regen</span>
                </>
              )}
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center justify-center px-3 sm:px-4 py-2 bg-success-500 text-white rounded-lg hover:bg-success-600 dark:bg-success-600 dark:hover:bg-success-700 transition-colors text-sm"
            >
              <PrinterIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              <span className="hidden sm:inline">Print All </span>
              {/* <span className="sm:hidden">Print</span> */}
            </button>
            <button
              onClick={onClose}
              className="text-secondary-500 hover:text-secondary-700 dark:text-secondary-400 dark:hover:text-secondary-200 p-1"
            >
              <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>
        </div>




        {/* Printable Content */}
        <div className="p-3 sm:p-6 max-h-[calc(95vh-140px)] sm:max-h-[calc(90vh-120px)] overflow-y-auto">
          <div ref={printRef}>
            {/* Print Header */}
            <div className="header text-center mb-6 sm:mb-8 print:block hidden">
              <h1 className="text-xl sm:text-2xl font-bold text-secondary-900 dark:text-white">
                Manufacturing QR Codes
              </h1>
              <p className="text-base sm:text-lg text-secondary-800 dark:text-secondary-200">
                Order {qrData.order.orderNumberNew || qrData.order.order_number_new || qrData.order.orderNumber || qrData.order.order_number} - {qrData.order.customerName || qrData.order.customer_name}
              </p>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                Generated on {new Date().toLocaleDateString()}
              </p>
            </div>

            {/* Controls Section */}
            <div className="flex flex-wrap items-center gap-4 mb-6 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg print:hidden">
              {/* <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">QR Code Size:</label>
                <select
                  value={selectedSize}
                  onChange={(e) => setSelectedSize(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="showIndividualPrint"
                  checked={showIndividualPrint}
                  onChange={(e) => setShowIndividualPrint(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="showIndividualPrint" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Show Individual Print Buttons
                </label>
              </div> */}
              <div className="flex items-center space-x-2 w-full max-w-sm">
                <DropdownField
                  label="Item Type"
                  name="itemType"
                  // fullWidth={true}
                  value={itemFilter}
                  required
                  // icon={<KeyIcon className="h-5 w-5 text-gray-500" />}
                  options={itemOption}
                  placeholder="Select Item Type"
                  onChange={(e) => {
                    setItemFilter(e.target.value);
                  }}
                //   console.log('Role dropdown changed - selected value:', role, 'roleOptions:', roleOptions);
                //   setFormData((prev) => ({
                //     ...prev,
                //     role: role as typeof prev.role,
                //   }));
                // }}
                />
              </div>
            </div>

            {/* QR Codes Grid */}
            {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 qr-grid">
              {allItems?.map((item, index) => (
                <div
                  key={item.id}
                  className="qr-item border-2 border-secondary-300 dark:border-secondary-600 p-4 sm:p-6 text-center bg-white dark:bg-secondary-800 rounded-lg"
                >
                  <div className="item-title font-bold text-base sm:text-lg mb-2 text-secondary-900 dark:text-white">
                    {item.type} #{index + 1}
                  </div>

                  <div className="qr-code mb-4">
                    <QRCodeSVG
                      value={item.qr_code || ""}
                      size={window.innerWidth < 640 ? 140 : 180}
                      level="M"
                      includeMargin={true}
                      className="mx-auto border border-secondary-200 dark:border-secondary-600"
                    />
                  </div>

                  <div className="item-info text-xs sm:text-sm space-y-1 text-secondary-700 dark:text-secondary-300">
                    <div>
                      <strong>Order No:</strong> {displayOrderNumber}
                    </div>
                    <div>
                      <strong>Product No:</strong> {(item as any).productNumber || ""}
                    </div>
                    <div>
                      <strong>Item No:</strong> {(item as any).itemNumber || ""}
                    </div>
                    <div>
                      <strong>Product Type:</strong> {item.productType}
                    </div>
                    <div>
                      <strong>Type:</strong> {item.type}
                    </div>
                    <div>
                      <strong>Item Number:</strong> {item.item_number}
                    </div>
                    <div>
                      <strong>Status:</strong> {formatStatusText(item.status)}
                    </div>
                    <div>
                      <strong>Current Stage:</strong> {item.current_stage}
                    </div>
                    <div className="qr-text mt-2 p-2 bg-secondary-100 dark:bg-secondary-700 rounded font-mono text-xs">
                      <strong>QR:</strong> {item.qr_code}
                    </div>
                    
            
                    
                    <div className="text-xs text-secondary-500 dark:text-secondary-400 mt-2">
                      <strong>Item ID:</strong> {item.id}
                    </div>
                  </div>

                  
                  <div className="mt-4 print:hidden">
                    <button
                      onClick={() => {
                        const singlePrintWindow = window.open("", "_blank");
                        if (!singlePrintWindow) return;

                        singlePrintWindow.document.write(`
                          <!DOCTYPE html>
                          <html>
                            <head>
                              <title>QR Code - ${item.type}</title>
                              <style>
                                body { 
                                  font-family: Arial, sans-serif; 
                                  margin: 20px; 
                                  text-align: center; 
                                  background: white;
                                }
                                .qr-single {
                                  border: 2px solid #000;
                                  padding: 30px;
                                  max-width: 400px;
                                  margin: 0 auto;
                                }
                                .item-title { font-size: 18px; font-weight: bold; margin-bottom: 20px; }
                                .qr-code { margin: 20px 0; }
                                .item-info { font-size: 14px; line-height: 1.6; text-align: left; }
                                .qr-text { 
                                  font-family: monospace; 
                                  font-size: 12px; 
                                  margin-top: 15px; 
                                  padding: 10px;
                                  background: #f0f0f0;
                                  word-break: break-all;
                                }
                              </style>
                            </head>
                            <body>
                              <div class="qr-single">
                                <div class="item-title">${item.type} - ${
                          item.productType
                        }</div>
                                <div class="qr-code">
                                  <svg width="200" height="200" xmlns="http://www.w3.org/2000/svg">
                                    <rect width="200" height="200" fill="white" stroke="black"/>
                                    <text x="100" y="100" text-anchor="middle" fill="black" font-size="10">${
                                      item.qr_code
                                    }</text>
                                  </svg>
                                </div>
                                <div class="item-info">
                                  <div><strong>Product Type:</strong> ${
                                    item.productType
                                  }</div>
                                  <div><strong>Type:</strong> ${item.type}</div>
                                  <div><strong>Item Number:</strong> ${item.item_number}</div>
                                  <div><strong>Status:</strong> ${formatStatusText(item.status)}</div>
                                  <div><strong>Stage:</strong> ${
                                    item.current_stage
                                  }</div>
                                  <div class="qr-text"><strong>QR Code:</strong> ${
                                    item.qr_code
                                  }</div>
                                  <div><strong>Item ID:</strong> ${
                                    item.id
                                  }</div>
                                </div>
                              </div>
                            </body>
                          </html>
                        `);

                        singlePrintWindow.document.close();
                        singlePrintWindow.focus();
                        setTimeout(() => {
                          singlePrintWindow.print();
                          singlePrintWindow.close();
                        }, 250);
                      }}
                      className="px-3 py-1 text-xs sm:text-sm bg-secondary-500 text-white rounded hover:bg-secondary-600 dark:bg-secondary-600 dark:hover:bg-secondary-500 transition-colors"
                    >
                      <span className="hidden sm:inline">Print Individual</span>
                      <span className="sm:hidden">Print</span>
                    </button>
                  </div>
                </div>
              ))}
            </div> */}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {filteredItems?.map((item, index) => (
                <div
                  key={item.id}
                  className="border-2 border-secondary-300 dark:border-secondary-600 
                            p-2 sm:p-4 text-center bg-white dark:bg-secondary-800 
                            rounded-lg flex flex-row items-center"
                >
                  {/* Left: Title + QR */}
                  <div className="w-[80px] sm:w-[120px] text-center">
                    <div className="qr-code">
                      {qrCodeImages[item.id] ? (
                        <img
                          src={qrCodeImages[item.id]}
                          alt="QR Code"
                          className="mx-auto border border-secondary-200 dark:border-secondary-600"
                          style={{
                            width: selectedSize === 'small' ? 60 : selectedSize === 'medium' ? 90 : 120,
                            height: selectedSize === 'small' ? 60 : selectedSize === 'medium' ? 90 : 120
                          }}
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center text-xs text-gray-500">
                          Loading...
                        </div>
                      )}
                    </div>
                  </div>



                  {/* Right: Info */}
                  <div className="ml-2 flex-1 text-left">
                    <div className="text-xs sm:text-sm  text-secondary-700 dark:text-secondary-300">
                      <div className="font-bold">
                        {item.itemNumber || item.item_number}
                      </div>

                      <div className="text-lg">
                        {qrData?.order?.siteNamePlate || qrData?.order?.site_name_plate || '-'}
                      </div>
                      <div className="text-lg">
                        {`${capitalizeWords(item?.type)} -  ${capitalizeWords(item.product?.type) || ''}`}
                      </div>
                      <div className="text-lg">
                        {(() => {
                          const width = (item as any)?.width || (item as any)?.specifications?.width || (item as any)?.product?.width || (item as any)?.dimensions?.width;
                          const height = (item as any)?.height || (item as any)?.specifications?.height || (item as any)?.product?.height || (item as any)?.dimensions?.height;
                          return width && height ? (
                            <>
                              {Math.round(width)}w x {Math.round(height)}h
                            </>
                          ) : null;
                        })()}
                      </div>
                      <div className="text-lg">
                        {
                          formatStatusText((item as any)?.product?.color ||
                            (item as any)?.product?.colour || '-')
                          // (item as any)?.product?.color || 
                          // (item as any)?.product?.colour || 
                          // '-'
                        }
                      </div>
                      {/* <div className="text-xs font-mono text-blue-600 break-all">{item.qr_code}</div> */}
                    </div>

                    {/* Individual Print Button */}
                    {showIndividualPrint && (
                      <div className="mt-2 print:hidden">
                        <button
                          onClick={async () => {
                            // try {
                            //   // Generate high-quality QR code
                            //   const qrCodeDataURL = await QRCode.toDataURL(item.qr_code, {
                            //     width: 400,
                            //     margin: 2,
                            //     color: {
                            //       dark: '#000000',
                            //       light: '#FFFFFF'
                            //     },
                            //     errorCorrectionLevel: 'H' // High error correction
                            //   });

                            //   // Create hidden iframe
                            //   const iframe = document.createElement('iframe');
                            //   iframe.style.position = 'absolute';
                            //   iframe.style.width = '0';
                            //   iframe.style.height = '0';
                            //   iframe.style.border = 'none';
                            //   document.body.appendChild(iframe);

                            //   const doc = iframe.contentWindow?.document;
                            //   if (!doc) return;

                            //   doc.open();
                            //   doc.write(`
                            //     <!DOCTYPE html>
                            //     <html>
                            //       <head>
                            //         <title>QR Code - ${item.item_number}</title>
                            //         <style>
                            //           body { font-family: Arial, sans-serif; margin: 0; padding: 20px; text-align: center; }
                            //           .qr-container { border: 2px solid #000; padding: 20px; margin: 20px auto; max-width: 500px; }
                            //           .qr-code { max-width: 400px; height: auto; margin: 20px 0; border: 1px solid #ccc; }
                            //           .item-details { text-align: left; margin-top: 15px; }
                            //           .item-details p { margin: 5px 0; }
                            //           @media print { body { margin: 0; padding: 10px; } .qr-container { margin: 10px auto; } }
                            //         </style>
                            //       </head>
                            //       <body>
                            //         <div class="qr-container">

                            //           <div class="qr-code">
                            //             <img src="${qrCodeDataURL}" alt="QR Code" />
                            //           </div>
                            //           <div class="item-details">
                            //             <p><strong>Item:</strong> ${item.item_number}</p>
                            //             <p><strong>Type:</strong> ${item.type}</p>
                            //             <p><strong>Product:</strong> ${item.product?.name || item.product?.type || '-'}</p>
                            //             <p><strong>Order:</strong> ${qrData?.order?.order_number_new || qrData?.order?.order_number || order.id.slice(-6)}</p>
                            //             <p><strong>Customer:</strong> ${qrData?.order?.customer_name || order.customer?.name}</p>
                            //             <p><strong>Dimensions:</strong> ${
                            //               (() => {
                            //                 const width = item.width || item.specifications?.width || item.product?.width || item.dimensions?.width;
                            //                 const height = item.height || item.specifications?.height || item.product?.height || item.dimensions?.height;
                            //                 return width && height ? `${Math.round(width)}x${Math.round(height)}` : '-';
                            //               })()
                            //             }</p>
                            //             <p><strong>Color:</strong> ${formatStatusText( item.color || item.specifications?.color || item.specifications?.colour || item.product?.color || item.product?.colour || '-')}</p>
                            //           </div>
                            //         </div>
                            //       </body>
                            //     </html>
                            //   `);
                            //   doc.close();

                            //   // Give browser a moment to render iframe content
                            //   setTimeout(() => {
                            //     iframe.contentWindow?.focus();
                            //     iframe.contentWindow?.print();
                            //     document.body.removeChild(iframe); // Clean up iframe
                            //   }, 300);

                            // } catch (error) {
                            //   console.error('Error generating QR code for print:', error);
                            //   alert('Error generating QR code for printing');
                            // }
                            handlePrintSingle(item)
                          }}
                          className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                        >
                          <PrinterIcon className="h-3 w-3 inline mr-1" />
                          Print
                        </button>

                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>


            {/* Print Footer */}
            <div className="text-center mt-8 print:block hidden">
              <p className="text-sm text-gray-600">
                Scan QR codes to track manufacturing progress • Generated by
                Production Workflow System
              </p>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="border-t border-secondary-200 dark:border-secondary-700 p-4 bg-secondary-50 dark:bg-secondary-700">
          <h3 className="font-medium mb-2 text-secondary-900 dark:text-white">
            Printing Instructions:
          </h3>
          <ul className="text-sm text-secondary-600 dark:text-secondary-400 space-y-1">
            <li>• Use "Print All" to print all QR codes on multiple pages</li>
            <li>• Use "Print Individual" for single item QR codes</li>
            <li>
              • Use waterproof stickers for durability in manufacturing
              environment
            </li>
            <li>• Ensure QR codes are clearly visible and not damaged</li>
            <li>• Place QR codes in accessible locations on items</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default QRCodePrintView;
